// @tennis-matches/ui/tournament-setup/steps – Gruppen+KO Step (shared)

import React, { useEffect, useRef } from "react";
import { ClubSeedingFields } from "./ClubSeedingFields.jsx";

function isPow2(x) {
  return Number.isInteger(x) && x >= 4 && (x & (x - 1)) === 0;
}

function nextPow2(x) {
  if (x <= 0) return 4;
  let p = 4;
  while (p < x) p *= 2;
  return Math.min(32, p);
}

/** Largest power of 2 in [4, 32] such that p <= n. */
function largestPow2UpTo(n) {
  if (n < 4) return 4;
  let p = 4;
  while (p * 2 <= n && p < 32) p *= 2;
  return Math.min(32, p);
}

/**
 * Best-fit: groupCount / advancePerGroup / includeBestThirds / bracketSize.
 * noByes: ZielPow2 = largest pow2 <= n; groupCount so that qualifiedTotal = bracketSize = ZielPow2, includeBestThirds <= groupCount.
 * byesOk: bracketSize = nextPow2(qualified).
 */
function bestFit(n, suggestPolicy = "noByes") {
  const advancePerGroup = 2;

  if (suggestPolicy === "byesOk") {
    let groupCount;
    if (n <= 8) groupCount = 2;
    else if (n <= 12) groupCount = 3;
    else if (n <= 16) groupCount = 4;
    else if (n <= 20) groupCount = 5;
    else if (n <= 24) groupCount = 6;
    else if (n <= 32) groupCount = 8;
    else groupCount = 8;
    const baseQualified = groupCount * advancePerGroup;
    const bracketSize = nextPow2(baseQualified);
    return { groupCount, advancePerGroup, includeBestThirds: 0, bracketSize };
  }

  // noByes: bracketSize = ZielPow2 (4/8/16/32), qualifiedTotal = ZielPow2, no byes
  const ZielPow2 = largestPow2UpTo(n);
  // groupCount so that groupCount*2 <= ZielPow2 and (ZielPow2 - groupCount*2) <= groupCount => groupCount >= ZielPow2/3
  const groupCount = Math.max(
    2,
    Math.min(Math.ceil(ZielPow2 / 3), Math.floor(ZielPow2 / 2)),
  );
  let includeBestThirds = ZielPow2 - groupCount * advancePerGroup;
  includeBestThirds = Math.max(0, Math.min(groupCount, includeBestThirds));
  const bracketSize = ZielPow2;
  return { groupCount, advancePerGroup, includeBestThirds, bracketSize };
}

/**
 * Step for configuring groups phase + knockout (Clubmeisterschaft format).
 * Props:
 * - t, value, onChange
 * - entrantsCount: number (effective entrants for best-fit)
 * - suggest: boolean (whether to auto-suggest when not touched)
 * - allowByes: boolean (default false)
 * - suggestPolicy: "noByes" | "byesOk" (default "noByes")
 * - validation: { ok, lines } from ctx.validation (optional, for "blocked by other step")
 * - stepOk: step-specific ok flags (optional)
 */
export function GroupsKoStep({
  t,
  value,
  onChange,
  entrantsCount,
  suggest,
  allowByes = false,
  suggestPolicy = "noByes",
  validation,
  stepOk,
  names,
  lkByPlayer,
}) {
  const groups = value?.groups ?? {};
  const qualification = value?.qualification ?? {};
  const ko = value?.ko ?? {};

  const markTouched = (next) => ({ ...(next ?? {}), __touched: true });
  const setGroups = (patch) =>
    onChange(markTouched({ ...value, groups: { ...groups, ...patch } }));
  const setQualification = (patch) =>
    onChange(
      markTouched({ ...value, qualification: { ...qualification, ...patch } }),
    );
  const setKo = (patch) =>
    onChange(markTouched({ ...value, ko: { ...ko, ...patch } }));

  const lastAppliedSuggestKeyRef = useRef(null);

  useEffect(() => {
    if (!suggest || value?.__touched || (entrantsCount ?? 0) < 4) return;
    const policy = suggestPolicy === "byesOk" ? "byesOk" : "noByes";
    const key = `${entrantsCount}:${policy}`;
    if (lastAppliedSuggestKeyRef.current === key) return;

    const bf = bestFit(entrantsCount ?? 0, policy);
    const g = value?.groups ?? {};
    const q = value?.qualification ?? {};
    const k = value?.ko ?? {};
    const already =
      Number(g.groupCount) === bf.groupCount &&
      Number(q.advancePerGroup) === bf.advancePerGroup &&
      Number(q.includeBestThirds ?? 0) === bf.includeBestThirds &&
      Number(k.bracketSize) === bf.bracketSize;

    lastAppliedSuggestKeyRef.current = key;
    if (already) return;

    onChange({
      groups: {
        ...g,
        groupCount: bf.groupCount,
        seedingMode: g.seedingMode ?? "random",
        allowUnevenGroups: g.allowUnevenGroups !== false,
      },
      qualification: {
        ...q,
        advancePerGroup: bf.advancePerGroup,
        includeBestThirds: bf.includeBestThirds,
      },
      ko: {
        ...k,
        bracketSize: bf.bracketSize,
        thirdPlaceMatch: !!k.thirdPlaceMatch,
        consolation: k.consolation ?? "none",
      },
    });
  }, [entrantsCount, suggest, suggestPolicy, value?.__touched]);

  const showSuggestedHint =
    suggest && !value?.__touched && (entrantsCount ?? 0) >= 4;

  const groupCount = Number(groups.groupCount ?? 0);
  const advancePerGroup = Number(qualification.advancePerGroup ?? 0);
  const includeBestThirds = Number(qualification.includeBestThirds ?? 0);
  const bracketSize = Number(ko.bracketSize ?? 0);

  const qualifiedTotal = Math.max(
    0,
    groupCount * advancePerGroup + includeBestThirds,
  );
  const isPow2Local = (n) =>
    Number.isInteger(n) && n > 0 && (n & (n - 1)) === 0;

  const localErrors = [];
  if (groupCount < 2)
    localErrors.push({ key: "club.groupsko.error.groupCount" });
  if (advancePerGroup < 1)
    localErrors.push({ key: "club.groupsko.error.advancePerGroup" });
  if (includeBestThirds > groupCount)
    localErrors.push({
      key: "club.groupsko.error.tooManyBestThirds",
      vars: { groups: groupCount },
    });
  if (!isPow2Local(bracketSize) || bracketSize < 4)
    localErrors.push({ key: "club.groupsko.error.bracketSize" });
  if (qualifiedTotal > bracketSize)
    localErrors.push({
      key: "club.groupsko.error.tooManyQualified",
      vars: { qualified: qualifiedTotal, bracket: bracketSize },
    });
  if (qualifiedTotal > 0 && bracketSize > 0 && bracketSize !== qualifiedTotal) {
    localErrors.push({
      key: "club.groupsko.error.bracketMustEqualQualified",
      vars: { qualified: qualifiedTotal },
    });
  }

  const localOk = localErrors.length === 0;

  return (
    <div className="wizardStep">
      <div className="tm-wizard-card tm-wizard-card--mb-lg">
        <span className="tm-wizard-card__label">
          {t("club.groupsko.status.title")}
        </span>
        <div className="tm-wizard-hint-box">
          <div>
            {t("club.groupsko.status.entrants", { n: entrantsCount ?? 0 })}
          </div>
          <div>
            {t("club.groupsko.status.qualified", { n: qualifiedTotal })}
          </div>
          <div>
            {t("club.groupsko.status.bracket", { n: bracketSize || 0 })}
          </div>
        </div>

        {!localOk ? (
          <div className="tm-wizard-error-box" role="alert" aria-live="polite">
            <p className="tm-wizard-error-box__title">
              {t("club.wizard.fixToContinue")}
            </p>
            <ul>
              {localErrors.map((e, idx) => (
                <li key={idx}>{t(e.key, e.vars)}</li>
              ))}
            </ul>
            <p className="tm-wizard-hint-box tm-wizard-hint-box--inline">
              {t("club.groupsko.info.qualifiedTotal", {
                qualified: qualifiedTotal,
              })}
            </p>
            {isPow2Local(qualifiedTotal) && qualifiedTotal >= 4 ? (
              <button
                type="button"
                className="tm-wizard-v0__btn tm-wizard-v0__btn--primary"
                onClick={() => setKo({ bracketSize: qualifiedTotal })}
              >
                {t("club.groupsko.fix.bracketToQualified", {
                  qualified: qualifiedTotal,
                })}
              </button>
            ) : null}
          </div>
        ) : null}

        {localOk && validation?.ok === false ? (
          <div className="tm-wizard-error-box" role="alert" aria-live="polite">
            <p className="tm-wizard-error-box__title">
              {t("club.wizard.blockedByOtherStep")}
            </p>
            <ul>
              {(Array.isArray(validation.lines) ? validation.lines : [])
                .slice(0, 6)
                .map((line, idx) => (
                  <li key={idx}>{String(line)}</li>
                ))}
            </ul>
          </div>
        ) : null}
      </div>

      {showSuggestedHint && (
        <p className="wizardStep__hint" data-hint="groupsko-suggested">
          {t("club.groupsko.suggested", { n: entrantsCount })}
        </p>
      )}
      <div className="wizardStep__section">
        <h3 className="wizardStep__title">{t("club.groupsko.groups.title")}</h3>

        <label className="wizardField">
          <span className="wizardField__label">
            {t("club.groupsko.groups.count")}
          </span>
          <input
            className="wizardField__input"
            type="number"
            min={2}
            max={16}
            value={groups.groupCount ?? 4}
            onChange={(e) => setGroups({ groupCount: Number(e.target.value) })}
          />
        </label>

        <ClubSeedingFields
          t={t}
          entrantsCount={entrantsCount}
          names={names}
          lkByPlayer={lkByPlayer}
          seedingMode={groups.seedingMode ?? "random"}
          seedCount={groups.seedCount ?? 0}
          seedingTouched={!!groups.__seedingTouched}
          onSeedingChange={({ seedingMode, seedCount, seedingTouched }) =>
            setGroups({
              seedingMode,
              seedCount,
              __seedingTouched: seedingTouched,
            })
          }
        />
      </div>

      <div className="wizardStep__section">
        <h3 className="wizardStep__title">
          {t("club.groupsko.qualification.title")}
        </h3>

        <label className="wizardField">
          <span className="wizardField__label">
            {t("club.groupsko.qualification.advance")}
          </span>
          <input
            className="wizardField__input"
            type="number"
            min={1}
            max={8}
            value={qualification.advancePerGroup ?? 2}
            onChange={(e) =>
              setQualification({ advancePerGroup: Number(e.target.value) })
            }
          />
        </label>

        <label className="wizardField">
          <span className="wizardField__label">
            {t("club.groupsko.qualification.bestThirds")}
          </span>
          <input
            className="wizardField__input"
            type="number"
            min={0}
            max={8}
            value={qualification.includeBestThirds ?? 0}
            onChange={(e) =>
              setQualification({ includeBestThirds: Number(e.target.value) })
            }
          />
        </label>
      </div>

      <div className="wizardStep__section">
        <h3 className="wizardStep__title">{t("club.groupsko.ko.title")}</h3>

        <label className="wizardField">
          <span className="wizardField__label">
            {t("club.groupsko.ko.bracketSize")}
          </span>
          <select
            className="wizardField__input"
            value={String(ko.bracketSize ?? 8)}
            onChange={(e) => setKo({ bracketSize: Number(e.target.value) })}
          >
            <option value="4">{t("club.groupsko.ko.bracket.4")}</option>
            <option value="8">{t("club.groupsko.ko.bracket.8")}</option>
            <option value="16">{t("club.groupsko.ko.bracket.16")}</option>
            <option value="32">{t("club.groupsko.ko.bracket.32")}</option>
          </select>
        </label>

        <label className="wizardField wizardField--checkbox">
          <input
            type="checkbox"
            checked={!!ko.thirdPlaceMatch}
            onChange={(e) => setKo({ thirdPlaceMatch: e.target.checked })}
          />
          <span>{t("club.groupsko.ko.thirdPlace")}</span>
        </label>
      </div>
    </div>
  );
}
